import { Controller } from "@hotwired/stimulus"

// Animated terminal simulation in the hero. Starts only once the terminal
// enters the viewport (saves CPU), then loops continuously.
export default class extends Controller {
  connect() {
    this.sequence = [
      { type: "cmd",  text: "buildio init ./novy-projekt", speed: 35, after: 320 },
      { type: "info", text: "→ resolving dependencies...", cls: "mut",  after: 520 },
      { type: "info", text: "✓ rails@8.0.5 installed",     cls: "ok",   after: 260 },
      { type: "info", text: "✓ postgres@16.2 linked",      cls: "ok",   after: 200 },
      { type: "info", text: "✓ turbo@8.0 · stimulus@3.2",  cls: "ok",   after: 200 },
      { type: "info", text: "✓ esbuild@0.24 installed",    cls: "ok",   after: 200 },
      { type: "info", text: "✓ bootstrap@5.3 installed",   cls: "ok",   after: 260 },
      { type: "blank", after: 320 },
      { type: "cmd",  text: "buildio build --prod", speed: 38, after: 400 },
      { type: "info", text: "→ compiling assets...", cls: "mut", after: 520 },
      { type: "info", text: "→ running tests ........ 248 passed", cls: "mut", after: 640 },
      { type: "info", text: "→ optimising bundle ....",  cls: "mut", after: 480 },
      { type: "info", text: "✓ bundle: 12.4 kB gzip",    cls: "ok",  after: 280 },
      { type: "blank", after: 320 },
      { type: "cmd",  text: "buildio ship", speed: 42, after: 380 },
      { type: "info", text: "→ pushing to production...", cls: "mut", after: 560 },
      { type: "info", text: "✓ deploy: buildio.cz",       cls: "ok",  after: 280 },
      { type: "info", text: "● server running on :443",   cls: "live", after: 360 },
      { type: "info", text: "● monitoring active",        cls: "live", after: 360 },
      { type: "blank", after: 2400 },
    ]

    this.running = false
    this.cursor = document.createElement("span")
    this.cursor.className = "term-cursor"

    this.io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !this.running) {
          this.running = true
          this.run()
        }
      }
    }, { threshold: 0.25 })
    this.io.observe(this.element)
  }

  disconnect() {
    if (this.io) this.io.disconnect()
    this.running = false
  }

  sleep(ms) { return new Promise((r) => setTimeout(r, ms)) }

  // Scroll only once per new line, on the next animation frame — avoids the
  // forced synchronous reflow that made the terminal stutter when it ran on
  // every typed character.
  scroll() {
    requestAnimationFrame(() => {
      this.element.scrollTop = this.element.scrollHeight
    })
  }

  appendLine() {
    const line = document.createElement("div")
    line.className = "term-line"
    this.element.appendChild(line)
    this.scroll()
    return line
  }

  async typeInto(el, text, speed) {
    for (const ch of text) {
      el.textContent += ch
      await this.sleep(speed + Math.random() * 20)
    }
  }

  async runCommand(step) {
    const line = this.appendLine()
    const sigil = document.createElement("span")
    sigil.className = "p-sigil"
    sigil.textContent = "$"
    line.appendChild(sigil)
    const cmd = document.createElement("span")
    cmd.className = "p-cmd"
    line.appendChild(cmd)
    line.appendChild(this.cursor)
    await this.typeInto(cmd, step.text, step.speed || 30)
  }

  addOutput(step) {
    const line = this.appendLine()
    const span = document.createElement("span")
    span.className = step.cls || "info"
    span.textContent = step.text
    line.appendChild(span)
  }

  addBlank() {
    const line = this.appendLine()
    line.innerHTML = "&nbsp;"
  }

  async run() {
    while (this.running) {
      for (const step of this.sequence) {
        if (!this.running) return
        if (step.type === "cmd")   await this.runCommand(step)
        if (step.type === "info")  this.addOutput(step)
        if (step.type === "blank") this.addBlank()
        await this.sleep(step.after || 100)
      }
      this.element.innerHTML = ""
    }
  }
}
